import React, { useState, useCallback } from 'react'
import { useTranslation } from '../../../../shared/hooks/useIntractionTranslate'
import { EnvelopeIcon, ArrowRightIcon, ClockIcon, CalendarIcon, ChevronDownIcon } from '@heroicons/react/24/outline'
import { Box } from '@mui/material'
import { LocalTimeBadge } from '../../../../shared/components/LocalTimeBadge'
import { TrainingSprintStepProps } from '../types'
import { INTERAX_COLORS } from '../../../../constants/colors'
import { PreviewEmailModal } from '../components/PreviewEmailModal'
import { NumberTracker, Button } from '../../../../interaxDesignSystemComponents'
import { useFlow } from '../../../../shared/components/FlowContainer/FlowContext'
import type { SprintEmailContent } from '../../../../../../pure-js/types/Sprint'
import {
  getDisplayDurationMinutes,
  getResolvedLoopCount,
  shouldShowLoopConfiguration
} from '../utils/activityConfiguration'

export const PreviewAndLaunchStep: React.FC<TrainingSprintStepProps> = ({
  data,
  updateData,
  stepId: _stepId,
  isActive: _isActive,
  isCompleted: _isCompleted,
  isVisited: _isVisited
}) => {
  const { t } = useTranslation()
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false)
  const [expandedTeamIds, setExpandedTeamIds] = useState<Set<string>>(new Set())
  const { goToNext, completeFlow, goToPrevious, canGoBack, state } = useFlow()

  const toggleTeam = (teamId: string) => {
    setExpandedTeamIds((prev) => {
      const next = new Set(prev)
      if (next.has(teamId)) {
        next.delete(teamId)
      } else {
        next.add(teamId)
      }
      return next
    })
  }

  const handleSaveEmailContent = useCallback(
    (emailContent: SprintEmailContent) => {
      updateData('emailContent', emailContent)
    },
    [updateData]
  )

  const participants = data.participants || []
  const teams = data.teams || []
  const activities = data.activities || []

  // Generate initials from name
  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase())
      .join('')
      .slice(0, 2)
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    const options: Intl.DateTimeFormatOptions = {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    }
    return date.toLocaleDateString('en-US', options)
  }

  const formatTime = (timeString: string) => {
    const [hours, minutes] = timeString.split(':')
    const hour24 = parseInt(hours)
    const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24
    const ampm = hour24 >= 12 ? 'PM' : 'AM'
    return `${hour12}:${minutes} ${ampm}`
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
      {/* Header */}
      <Box sx={{ textAlign: 'center' }}>
        <Box className="text-headline-2" sx={{ color: INTERAX_COLORS.white, marginBottom: '16px' }}>
          {t('facilitator.trainingSprint.previewAndLaunch.title')}
        </Box>
        <Box className="text-body-1" sx={{ color: INTERAX_COLORS.thunderGrey }}>
          {t('facilitator.trainingSprint.previewAndLaunch.subtitle')}
        </Box>
      </Box>

      {/* Box 1: Sprint Name */}
      <Box
        sx={{
          backgroundColor: INTERAX_COLORS.heritageBlue40,
          borderColor: INTERAX_COLORS.heritageBlue,
          borderWidth: '1px',
          borderStyle: 'solid',
          borderRadius: '8px',
          padding: '24px'
        }}
      >
        <Box className="text-body-2" sx={{ color: INTERAX_COLORS.white, marginBottom: '12px' }}>
          {t('facilitator.trainingSprint.previewAndLaunch.sprintName')}:
        </Box>
        <Box
          sx={{
            padding: '16px',
            backgroundColor: INTERAX_COLORS.heritageBlue,
            borderColor: INTERAX_COLORS.heritageBlue,
            borderWidth: '1px',
            borderStyle: 'solid',
            borderRadius: '8px',
            color: INTERAX_COLORS.white,
            fontFamily: 'Inter, sans-serif',
            fontSize: '18px',
            fontWeight: '600',
            lineHeight: '1.5'
          }}
        >
          {data.sprintName || t('facilitator.trainingSprint.previewAndLaunch.noSprintName')}
        </Box>
      </Box>

      {/* Box 2: Sprint Overview */}
      <Box
        sx={{
          backgroundColor: INTERAX_COLORS.heritageBlue40,
          borderColor: INTERAX_COLORS.heritageBlue,
          borderWidth: '1px',
          borderStyle: 'solid',
          borderRadius: '8px',
          padding: '24px'
        }}
      >
        <Box className="text-headline-3" sx={{ color: INTERAX_COLORS.white, marginBottom: '24px' }}>
          {t('facilitator.trainingSprint.previewAndLaunch.sprintOverview')} ({activities.length}{' '}
          {t('facilitator.trainingSprint.previewAndLaunch.activities')})
        </Box>

        {activities.length === 0 ? (
          <Box sx={{ textAlign: 'center', padding: '48px 0' }}>
            <Box className="text-body-2" sx={{ color: INTERAX_COLORS.thunderGrey }}>
              {t('facilitator.trainingSprint.previewAndLaunch.noActivitiesScheduled')}
            </Box>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', gap: '24px' }}>
            {/* Number Tracker */}
            <NumberTracker items={activities} itemHeight={80} itemSpacing={16} visible={true} />

            {/* Activities List */}
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {activities.map((activity) =>
                (() => {
                  const loopCount = shouldShowLoopConfiguration(activity.type)
                    ? getResolvedLoopCount(activity.type, activity.numberOfLoops)
                    : undefined
                  const displayDuration = getDisplayDurationMinutes(activity)

                  return (
                    <Box
                      key={activity.id}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '16px',
                        padding: '16px',
                        backgroundColor: INTERAX_COLORS.heritageBlue,
                        borderColor: INTERAX_COLORS.heritageBlue,
                        borderWidth: '1px',
                        borderStyle: 'solid',
                        borderRadius: '8px'
                      }}
                    >
                      {/* Clock icon */}
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: '40px',
                          height: '40px',
                          backgroundColor: INTERAX_COLORS.sun,
                          borderRadius: '8px',
                          flexShrink: 0
                        }}
                      >
                        <ClockIcon className="w-6 h-6" style={{ color: INTERAX_COLORS.offBlack }} />
                      </Box>

                      {/* Activity details */}
                      <Box sx={{ flex: 1 }}>
                        <Box className="text-body-2" sx={{ color: INTERAX_COLORS.white, marginBottom: '4px' }}>
                          {activity.title}
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <ClockIcon className="w-4 h-4" style={{ color: INTERAX_COLORS.thunderGrey }} />
                            <Box className="text-body-3" sx={{ color: INTERAX_COLORS.thunderGrey }}>
                              {displayDuration} {t('facilitator.trainingSprint.previewAndLaunch.min')}
                            </Box>
                          </Box>
                          {loopCount ? (
                            <Box className="text-body-3" sx={{ color: INTERAX_COLORS.thunderGrey }}>
                              {t('facilitator.activityConfig.loopCountSummary', { count: loopCount })}
                            </Box>
                          ) : null}
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <CalendarIcon className="w-4 h-4" style={{ color: INTERAX_COLORS.thunderGrey }} />
                            <Box className="text-body-3" sx={{ color: INTERAX_COLORS.thunderGrey }}>
                              {activity.scheduledDate && formatDate(activity.scheduledDate)} •{' '}
                              {activity.scheduledTime && formatTime(activity.scheduledTime)}
                            </Box>
                            <LocalTimeBadge variant="scheduling" />
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  )
                })()
              )}
            </Box>
          </Box>
        )}
      </Box>

      {/* Box 3: Team Assignments */}
      <Box
        sx={{
          backgroundColor: INTERAX_COLORS.heritageBlue40,
          borderColor: INTERAX_COLORS.heritageBlue,
          borderWidth: '1px',
          borderStyle: 'solid',
          borderRadius: '8px',
          padding: '24px'
        }}
      >
        <Box className="text-headline-3" sx={{ color: INTERAX_COLORS.white, marginBottom: '24px' }}>
          {t('facilitator.trainingSprint.previewAndLaunch.teamAssignments')}
        </Box>

        {teams.length === 0 ? (
          <Box sx={{ textAlign: 'center', padding: '48px 0' }}>
            <Box className="text-body-2" sx={{ color: INTERAX_COLORS.thunderGrey }}>
              {t('facilitator.trainingSprint.previewAndLaunch.noTeamsCreated')}
            </Box>
          </Box>
        ) : (
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '16px' }}>
            {teams.map((team) => {
              const isExpanded = expandedTeamIds.has(team.id)
              return (
                <Box
                  key={team.id}
                  sx={{
                    backgroundColor: INTERAX_COLORS.heritageBlue,
                    borderColor: INTERAX_COLORS.heritageBlue,
                    borderWidth: '1px',
                    borderStyle: 'solid',
                    borderRadius: '8px',
                    overflow: 'hidden'
                  }}
                >
                  {/* Clickable header */}
                  <Box
                    onClick={() => toggleTeam(team.id)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '16px',
                      cursor: 'pointer',
                      borderBottom: isExpanded ? `1px solid ${INTERAX_COLORS.heritageBlue40}` : 'none',
                      '&:hover': { opacity: 0.85 }
                    }}
                  >
                    <Box className="text-body-2" sx={{ color: INTERAX_COLORS.white }}>
                      {team.name}
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Box className="text-body-3" sx={{ color: INTERAX_COLORS.thunderGrey }}>
                        {team.participants.length} {t('facilitator.trainingSprint.previewAndLaunch.members')}
                      </Box>
                      <ChevronDownIcon
                        style={{
                          width: '16px',
                          height: '16px',
                          color: INTERAX_COLORS.sun,
                          flexShrink: 0,
                          transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                          transition: 'transform 200ms'
                        }}
                      />
                    </Box>
                  </Box>

                  {/* Content: avatar row (collapsed) or member list (expanded) */}
                  {isExpanded ? (
                    <Box>
                      {team.participants.map((participant, idx) => (
                        <Box
                          key={participant.id}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px',
                            padding: '10px 16px',
                            borderBottom:
                              idx < team.participants.length - 1 ? `1px solid ${INTERAX_COLORS.heritageBlue40}` : 'none'
                          }}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              width: '32px',
                              height: '32px',
                              backgroundColor: INTERAX_COLORS.sun,
                              borderRadius: '50%',
                              flexShrink: 0
                            }}
                          >
                            <Box className="text-body-3" sx={{ color: INTERAX_COLORS.offBlack, fontWeight: '600' }}>
                              {getInitials(participant.name)}
                            </Box>
                          </Box>
                          <Box sx={{ minWidth: 0 }}>
                            <Box className="text-body-2" sx={{ color: INTERAX_COLORS.white }}>
                              {participant.name}
                            </Box>
                            <Box className="text-body-3" sx={{ color: INTERAX_COLORS.thunderGrey }}>
                              {participant.email}
                            </Box>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  ) : (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px', padding: '12px 16px 16px' }}>
                      {team.participants.map((participant) => (
                        <Box
                          key={participant.id}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '32px',
                            height: '32px',
                            backgroundColor: INTERAX_COLORS.sun,
                            borderRadius: '50%',
                            flexShrink: 0
                          }}
                          title={participant.name}
                        >
                          <Box className="text-body-3" sx={{ color: INTERAX_COLORS.offBlack, fontWeight: '600' }}>
                            {getInitials(participant.name)}
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
              )
            })}
          </Box>
        )}
      </Box>

      {/* Box 4: Ready to Launch */}
      <Box
        sx={{
          backgroundColor: INTERAX_COLORS.heritageBlue40,
          borderColor: INTERAX_COLORS.heritageBlue,
          borderWidth: '1px',
          borderStyle: 'solid',
          borderRadius: '8px',
          padding: '24px'
        }}
      >
        <Box className="text-headline-3" sx={{ color: INTERAX_COLORS.white, marginBottom: '16px' }}>
          {t('facilitator.trainingSprint.previewAndLaunch.readyToLaunch')}?
        </Box>

        <Box className="text-body-2" sx={{ color: INTERAX_COLORS.thunderGrey, marginBottom: '16px' }}>
          {t('facilitator.trainingSprint.previewAndLaunch.launchDescription', { count: participants.length })}
        </Box>
        <Box
          className="text-body-3"
          sx={{ color: INTERAX_COLORS.thunderGrey, marginBottom: { xs: '20px', sm: '24px', md: '24px' } }}
        >
          {t('facilitator.trainingSprint.previewAndLaunch.launchWithoutInvitationsHint')}
        </Box>

        <Box sx={{ containerType: 'inline-size' }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row', md: 'row' },
              flexWrap: { xs: 'nowrap', sm: 'wrap', md: 'wrap' },
              gap: '16px',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            {canGoBack() && (
              <Box sx={{ mr: { xs: 0, sm: 'auto', md: 'auto' } }}>
                <Button
                  style={{ color: INTERAX_COLORS.white }}
                  variant="secondary"
                  onClick={goToPrevious}
                  icon={<ArrowRightIcon className="w-5 h-5 rotate-180" />}
                  iconPosition="left"
                >
                  <Box
                    component="span"
                    sx={{
                      '@container (min-width: 800px) and (max-width: 999px)': {
                        position: 'absolute',
                        width: '1px',
                        height: '1px',
                        padding: 0,
                        margin: '-1px',
                        overflow: 'hidden',
                        clip: 'rect(0 0 0 0)',
                        whiteSpace: 'nowrap',
                        border: 0
                      }
                    }}
                  >
                    {t('common.previous')}
                  </Box>
                </Button>
              </Box>
            )}
            <Button
              variant="secondary"
              onClick={() => setIsPreviewModalOpen(true)}
              icon={<EnvelopeIcon className="w-5 h-5" />}
              iconPosition="left"
            >
              {t('facilitator.trainingSprint.emailEditor.customizeEmail')}
            </Button>
            <Button
              variant="secondary"
              onClick={() => completeFlow({ sendInvitations: false })}
              icon={<ArrowRightIcon className="w-5 h-5" />}
              iconPosition="right"
              disabled={state.isLoading}
              data-testid="launch-without-invites-btn"
            >
              {t('facilitator.trainingSprint.previewAndLaunch.launchWithoutInvitations')}
            </Button>
            <Button
              variant="primary"
              onClick={goToNext}
              icon={<ArrowRightIcon className="w-5 h-5" />}
              iconPosition="right"
              disabled={state.isLoading}
              data-testid="launch-and-send-btn"
            >
              {t('facilitator.trainingSprint.previewAndLaunch.launchAndSend')}
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Preview Email Modal */}
      <PreviewEmailModal
        isOpen={isPreviewModalOpen}
        onClose={() => setIsPreviewModalOpen(false)}
        sprintData={data}
        onSave={handleSaveEmailContent}
      />
    </Box>
  )
}
